
//#region import libraries
import { useEffect, useState } from "react";
//redux
import { useDispatch, useSelector } from "react-redux";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { checkIfStaticReasons, createRejectReasons, getRejectReasons, resetStatus, selectRejectReasonsStatus, updateRejectReasons } from "_redux/slices/timeline/reject-reasons";
import { Colors } from "_styles";
//bootstrap
import { Button, Card, Col, Form, Row } from "react-bootstrap";
//constants
import { DEFAULT_RR_ELIGIBLE, DEFAULT_RR_TARGETS, MOCK_REJECT_REASONS, BFS_MOCK_REJECT_REASONS } from "_utils/constants";
//#endregion

//use this to initialize new reason, then controller fill it
function makeEmptyReason() {
    return {
        // no `value` for new reasons
        label: "",
        target: [], // array of target strings
        eligible: [], // array of eligible strings
        _localId: `local_${Date.now()}_${Math.random()}`, // useful for keys
    };
}

const RejectReasonsForm = ({ selectedReason, setShowModal }) => {
    //#region redux
    const dispatch = useDispatch()
    const dbUser = useSelector(selectFirebaseInfo);
    const isStaticReasons = useSelector(checkIfStaticReasons);                //check if airport has static or custom reject reasons, to check if we will use post or patch action
    const status = useSelector(selectRejectReasonsStatus);                     //status for getting/posting and patching reject reasons, then dispatch get reasons if was succeeded
    //#endregion

    //#region local state
    const [reasons, setReasons] = useState([])        //display one reason to update
    const [errors, setErrors] = useState({});         // keyed by _localId or value
    const [saving, setSaving] = useState(false);
    //#endregion


    //#region useEffect
    //reset status after  getting/posting and updating reasons
    useEffect(() => {
        if (status === "succeeded") {
            dispatch(resetStatus())
        }
    }, [])

    // Initialize reasons: selectedReason.reasons if provided, else static data if flagged, else one empty row
    useEffect(() => {
        if (selectedReason) {
            setReasons([selectedReason]);
        }
        //should we display static data or not??
        else if (isStaticReasons) {
            // use static defaults - BFS has additional reasons
            const iata = dbUser?.details?.authority;
            const staticReasons = iata === "BFS" ? BFS_MOCK_REJECT_REASONS : MOCK_REJECT_REASONS;
            // _localId is already included in the constants with stable identifiers
            setReasons(staticReasons);
        } else {
            setReasons([makeEmptyReason()]);
        }
    }, [selectedReason, isStaticReasons, dbUser?.details?.authority]);

    //fetching data after posting/patching and close modal, so we make sure that data is up to date between portal&DB
    useEffect(() => {
        if (saving && status === "succeeded") {
            setSaving(false);
            setShowModal(false);
            const params = {
                source: "ALL",
                airport: dbUser.details.authority
            }
            dispatch(getRejectReasons(params))
        }
    }, [saving, status])
    //#endregion

    //#region functions
    // helpers to update reasons list
    const updateReason = (idOrValue, patch) => {
        setReasons((prev) =>
            prev.map((r) => (r._localId === idOrValue || r.value === idOrValue ? { ...r, ...patch } : r))
        );
    };

    const addReason = () => {
        setReasons((prev) => [...prev, makeEmptyReason()]);
    };

    const removeReason = (idOrValue) => {
        setReasons((prev) => prev.filter((r) => !(r._localId === idOrValue || r.value === idOrValue)));
    };

    const toggleInArray = (arr = [], value) => {
        if (!Array.isArray(arr)) arr = [];
        return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    };

    // Validation
    //check if each reason has, at list eligible.target and label properties, which has a valid value    
    const validate = () => {
        const nextErrors = {};
        reasons.forEach((r) => {
            const key = r._localId || r.value || JSON.stringify(r);
            if (!r.label || !r.label.trim()) {
                nextErrors[key] = "Label is required";
                return;
            }
            if (!Array.isArray(r.target) || r.target.length === 0) {
                nextErrors[key] = "Pick at least one target";
                return;
            }
            if (!Array.isArray(r.eligible) || r.eligible.length === 0) {
                nextErrors[key] = "Pick at least one eligible option";
                return;
            }
        });
        setErrors(nextErrors);
        // no errors => valid
        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = async () => {
        //validate field/body
        if (!validate()) return;

        const body = {
            airport: dbUser.details.authority,
            reasons: reasons.map(({ label, target, eligible, value }) => {
                // Keep value only if present (existing reasons). New ones should not include `value` (server will generate)
                const item = {
                    label: (label || "").trim(),
                    target: Array.isArray(target) ? target : [],
                    eligible: Array.isArray(eligible) ? eligible : []
                };
                if (value) item.value = value;//This in case we updated old reason, no new reasons added
                return item;
            }),
        };
        //update loading status
        setSaving(true);

        // If isStaticReasons true, no doc in DB yet, then need to create           
        if (isStaticReasons) {
            // create new document
            dispatch(createRejectReasons(body));
        } else {
            // patch existing document 

            dispatch(updateRejectReasons(body));
        }

    };
    //#endregion

    //#region render form
    return (
        <div>
            <Card className="mb-3">
                <Card.Body>
                    <Row className="mb-2">
                        <Col>
                            <h5 style={{ color: Colors.PRM_LIGHT_BLUE }}>Reject Reasons</h5>
                        </Col>
                        <Col className="text-end">
                            <>
                                <Button variant="outline-primary" size="sm" onClick={addReason}>
                                    + Add reason
                                </Button>{" "}
                                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                                    {saving ? "Saving..." : "Save"}
                                </Button>
                            </>

                        </Col>
                    </Row>

                    {reasons.map((reason, idx) => {
                        const err = errors[reason._localId] || errors[reason.value];
                        return (
                            <Card key={reason._localId} className="mb-3">
                                <Card.Body>
                                    {/* Row includes label and also eligible */}
                                    <Row className="align-items-start">
                                        <Col md={9}>
                                            <Form.Group>
                                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Reason label</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Enter reason label"
                                                    value={reason.label}
                                                    onChange={(e) => updateReason(reason._localId, { label: e.target.value })}
                                                />

                                            </Form.Group>
                                        </Col>

                                        <Col md={3}>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Eligible</Form.Label>
                                            <div>
                                                {DEFAULT_RR_ELIGIBLE.map((e) => (
                                                    <Form.Check
                                                        key={e}
                                                        inline
                                                        label={e}
                                                        style={{ size: "12" }}
                                                        type="checkbox"
                                                        checked={Array.isArray(reason.eligible) && reason.eligible.includes(e)}
                                                        onChange={() =>
                                                            updateReason(reason._localId, { eligible: toggleInArray(reason.eligible, e) })
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>
                                    {/* Row includes Target list */}
                                    <Row>
                                        <Col md={12}>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Target</Form.Label>
                                            <div>
                                                {DEFAULT_RR_TARGETS.map((t) => (
                                                    <Form.Check
                                                        key={t}
                                                        inline
                                                        label={t}
                                                        type="checkbox"
                                                        checked={Array.isArray(reason.target) && reason.target.includes(t)}
                                                        onChange={() =>
                                                            updateReason(reason._localId, { target: toggleInArray(reason.target, t) })
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Col>

                                    </Row>
                                    {/* remove button for local reason to update/edit */}
                                    <Row className="mt-2">
                                        <Col>
                                            {err && <div className="text-danger small">{err}</div>}
                                        </Col>
                                        <Col className="text-end">

                                            <Button variant="outline-danger" size="sm" onClick={() => removeReason(reason._localId)}>
                                                Remove
                                            </Button>

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </Card.Body>
            </Card>
        </div>
    );

    //#endregion
}

export default RejectReasonsForm